import * as THREE from 'three';

/**
 * 2D Simplex noise implementation (public domain, based on Stefan Gustavson's algorithm)
 */
class SimplexNoise {
    constructor(seed = 0) {
        // Gradients for 2D simplex noise
        this.grad2 = [
            [1, 1], [-1, 1], [1, -1], [-1, -1],
            [1, 0], [-1, 0], [0, 1], [0, -1]
        ];
        // Build permutation table from seed
        const p = new Uint8Array(256);
        for (let i = 0; i < 256; i++) p[i] = i;
        // Seeded Fisher-Yates shuffle
        let s = seed;
        for (let i = 255; i > 0; i--) {
            s = (s * 16807 + 0) % 2147483647; // LCG
            const j = s % (i + 1);
            [p[i], p[j]] = [p[j], p[i]];
        }
        this.perm = new Uint8Array(512);
        for (let i = 0; i < 512; i++) this.perm[i] = p[i & 255];
    }

    noise2D(x, y) {
        const F2 = 0.5 * (Math.sqrt(3) - 1);
        const G2 = (3 - Math.sqrt(3)) / 6;

        // Skew input space
        const s = (x + y) * F2;
        const i = Math.floor(x + s);
        const j = Math.floor(y + s);

        // Unskew cell origin back to (x, y) space
        const t = (i + j) * G2;
        const X0 = i - t;
        const Y0 = j - t;
        const x0 = x - X0;
        const y0 = y - Y0;

        // Determine which simplex we are in
        const i1 = x0 > y0 ? 1 : 0;
        const j1 = x0 > y0 ? 0 : 1;

        const x1 = x0 - i1 + G2;
        const y1 = y0 - j1 + G2;
        const x2 = x0 - 1 + 2 * G2;
        const y2 = y0 - 1 + 2 * G2;

        // Hash coordinates of the three simplex corners
        const ii = i & 255;
        const jj = j & 255;
        const gi0 = this.perm[ii + this.perm[jj]] % 8;
        const gi1 = this.perm[ii + i1 + this.perm[jj + j1]] % 8;
        const gi2 = this.perm[ii + 1 + this.perm[jj + 1]] % 8;

        // Calculate contribution from three corners
        let n0 = 0, n1 = 0, n2 = 0;

        let t0 = 0.5 - x0 * x0 - y0 * y0;
        if (t0 > 0) {
            t0 *= t0;
            n0 = t0 * t0 * (this.grad2[gi0][0] * x0 + this.grad2[gi0][1] * y0);
        }

        let t1 = 0.5 - x1 * x1 - y1 * y1;
        if (t1 > 0) {
            t1 *= t1;
            n1 = t1 * t1 * (this.grad2[gi1][0] * x1 + this.grad2[gi1][1] * y1);
        }

        let t2 = 0.5 - x2 * x2 - y2 * y2;
        if (t2 > 0) {
            t2 *= t2;
            n2 = t2 * t2 * (this.grad2[gi2][0] * x2 + this.grad2[gi2][1] * y2);
        }

        // Scale to [-1, 1]
        return 70 * (n0 + n1 + n2);
    }
}

/**
 * TerrainHeightMap — generates and queries rolling desert dune terrain.
 *
 * Usage:
 *   const heightMap = new TerrainHeightMap({ terrainSize: 2000, seed: 42 });
 *   const y = heightMap.getHeight(worldX, worldZ);
 *   const normal = heightMap.getNormal(worldX, worldZ);
 */
export default class TerrainHeightMap {
    constructor(options = {}) {
        this.terrainSize = options.terrainSize || 2000;
        this.seed = options.seed || 42;
        this.simplex = new SimplexNoise(this.seed);

        // Noise octaves — layered for natural desert dunes
        this.octaves = [
            { frequency: 0.002, amplitude: 12 },   // Large rolling dunes (~500 unit wavelength)
            { frequency: 0.005, amplitude: 5 },     // Medium undulations (~200 unit wavelength)
            { frequency: 0.015, amplitude: 1.5 },   // Small ripples (~67 unit wavelength)
        ];

        // Flat zone around spawn point (0,0)
        this.flatZoneRadius = 80;
        this.flatZoneFalloff = 40;

        // Simple cache for repeated lookups
        this._cache = new Map();
        this._cacheMaxSize = 10000;
    }

    /**
     * Returns terrain height (Y) at world position (x, z).
     */
    getHeight(x, z) {
        // Quantize key to 0.1 resolution for cache efficiency
        const kx = Math.round(x * 10);
        const kz = Math.round(z * 10);
        const key = (kx << 16) ^ kz; // fast integer hash
        if (this._cache.has(key)) return this._cache.get(key);

        // Sum noise octaves
        let height = 0;
        for (let i = 0; i < this.octaves.length; i++) {
            const oct = this.octaves[i];
            height += this.simplex.noise2D(x * oct.frequency, z * oct.frequency) * oct.amplitude;
        }

        // Flatten near spawn (0,0) for smooth start area
        const distFromCenter = Math.sqrt(x * x + z * z);
        if (distFromCenter < this.flatZoneRadius) {
            height = 0;
        } else if (distFromCenter < this.flatZoneRadius + this.flatZoneFalloff) {
            const t = (distFromCenter - this.flatZoneRadius) / this.flatZoneFalloff;
            height *= t * t * (3 - 2 * t); // smoothstep
        }

        // Fade edges to avoid visible terrain boundary seams
        const halfSize = this.terrainSize / 2;
        const edgeMargin = 50;
        const edgeFadeX = Math.min(1, Math.max(0, (halfSize - Math.abs(x)) / edgeMargin));
        const edgeFadeZ = Math.min(1, Math.max(0, (halfSize - Math.abs(z)) / edgeMargin));
        height *= Math.min(edgeFadeX, edgeFadeZ);

        // Cache result
        if (this._cache.size > this._cacheMaxSize) this._cache.clear();
        this._cache.set(key, height);

        return height;
    }

    /**
     * Returns the terrain surface normal at world position (x, z).
     * Computed via central-difference sampling.
     */
    getNormal(x, z) {
        const d = 1.0; // sample spacing
        const hL = this.getHeight(x - d, z);
        const hR = this.getHeight(x + d, z);
        const hD = this.getHeight(x, z - d);
        const hU = this.getHeight(x, z + d);

        return new THREE.Vector3(hL - hR, 2 * d, hD - hU).normalize();
    }
}
